﻿/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  StatusBar,
  Alert,
  Dimensions,
  Animated,
  TextInput,
  Modal,
  RefreshControl,
  ActivityIndicator,
  Platform,
  Linking,
  Share,
  Image,
  TouchableWithoutFeedback,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo from '@react-native-community/netinfo';
import {
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { SafeAreaView } from 'react-native-safe-area-context';
import { VideoView, useVideoPlayer } from 'expo-video';
import { WebView } from 'react-native-webview';

const { width, height } = Dimensions.get('window');

// ─── YouTube support ─────────────────────────────────────────────────────────
// Extract a YouTube video id from any common URL shape (watch, youtu.be,
// embed, shorts). Returns null if the URL isn't a YouTube link.
const getYouTubeId = (url) => {
  if (!url || typeof url !== 'string') return null;
  const patterns = [
    /(?:youtube\.com\/watch\?(?:.*&)?v=)([A-Za-z0-9_-]{11})/,
    /(?:youtu\.be\/)([A-Za-z0-9_-]{11})/,
    /(?:youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/,
    /(?:youtube\.com\/shorts\/)([A-Za-z0-9_-]{11})/,
    /(?:youtube\.com\/v\/)([A-Za-z0-9_-]{11})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m && m[1]) return m[1];
  }
  return null;
};

const isYouTubeUrl = (url) => !!getYouTubeId(url);

// HTML that embeds the YouTube iframe player, sized to fill the WebView.
const youTubeEmbedHtml = (videoId) => `<!DOCTYPE html><html><head>
<meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">
<style>*{margin:0;padding:0;background:#000}html,body{height:100%;overflow:hidden}
.wrap{position:relative;width:100%;height:100%}iframe{position:absolute;top:0;left:0;width:100%;height:100%;border:0}</style>
</head><body><div class="wrap">
<iframe src="https://www.youtube.com/embed/${videoId}?playsinline=1&rel=0&modestbranding=1&autoplay=1"
 allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe>
</div></body></html>`;

// ─── Connection ──────────────────────────────────────────────────────────────
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const VideoLibrary = () => {
  const navigation = useNavigation();
  // Coerce DB string fields (comma-separated) or arrays into a string array.
  const toArray = (val) => {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
      const s = val.trim();
      if (!s) return [];
      if (s.startsWith('[')) { try { const p = JSON.parse(s); if (Array.isArray(p)) return p; } catch {} }
      return s.split(',').map((x) => x.trim()).filter(Boolean);
    }
    return [];
  };
  const [darkMode, setDarkMode] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOnline, setIsOnline] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedFilter, setSelectedFilter] = useState('recent');
  const [bookmarkedVideos, setBookmarkedVideos] = useState([]);
  const [watchedVideos, setWatchedVideos] = useState([]);
  const [downloadedVideos, setDownloadedVideos] = useState([]);
  
  // Video player states
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackPosition, setPlaybackPosition] = useState(0);
  const [playbackDuration, setPlaybackDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const videoViewRef = useRef(null);
  const player = useVideoPlayer(null, (p) => {
    if (p) { p.loop = false; p.timeUpdateEventInterval = 0.5; }
  });
  
  // Animation values
  const [fadeAnim] = useState(new Animated.Value(0));
  const [slideAnim] = useState(new Animated.Value(50));
  
  const [videos, setVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    watched: 0,
    downloaded: 0,
    totalWatchTime: 0,
  });

  const videoCategories = [
    { id: 'all', label: 'All Videos', icon: 'play-circle', color: '#667eea' },
    { id: 'lectures', label: 'Lectures', icon: 'school', color: '#3b82f6' },
    { id: 'tutorials', label: 'Tutorials', icon: 'book-open', color: '#10b981' },
    { id: 'demonstrations', label: 'Demos', icon: 'flask', color: '#8b5cf6' },
    { id: 'software', label: 'Software', icon: 'laptop', color: '#ec4899' },
    { id: 'calculations', label: 'Calculations', icon: 'calculator', color: '#f59e0b' },
    { id: 'interviews', label: 'Interviews', icon: 'account-voice', color: '#ef4444' },
    { id: 'research', label: 'Research', icon: 'microscope', color: '#06b6d4' },
    { id: 'conferences', label: 'Conferences', icon: 'presentation', color: '#14b8a6' },
    { id: 'projects', label: 'Projects', icon: 'hammer-wrench', color: '#f97316' },
  ];

  const filters = [
    { id: 'recent', label: 'Recent', icon: 'history' },
    { id: 'popular', label: 'Popular', icon: 'fire' },
    { id: 'trending', label: 'Trending', icon: 'trending-up' },
    { id: 'featured', label: 'Featured', icon: 'star' },
    { id: 'bookmarked', label: 'Bookmarked', icon: 'bookmark' },
    { id: 'downloaded', label: 'Downloaded', icon: 'download' },
    { id: 'watched', label: 'Watched', icon: 'eye-check' },
    { id: 'unwatched', label: 'Unwatched', icon: 'eye-off' },
  ];

  const playbackSpeeds = [0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0];

  useEffect(() => {
    loadInitialData();
    setupNetInfoListener();
    startAnimations();
    
    return () => {
      const unsubscribe = NetInfo.addEventListener(state => {});
      if (unsubscribe) unsubscribe();
      try { if (player) player.pause(); } catch (e) {}
    };
  }, []);

  useEffect(() => {
    filterVideos();
  }, [searchQuery, selectedCategory, selectedFilter, videos]);

  useFocusEffect(
    React.useCallback(() => {
      refreshData();
      return () => {
        try { if (player) player.pause(); } catch (e) {}
      };
    }, [])
  );

  const setupNetInfoListener = () => {
    const unsubscribe = NetInfo.addEventListener(state => {
      setIsOnline(state.isConnected);
    });
    return unsubscribe;
  };

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 600,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([
        loadVideos(),
        loadBookmarks(),
        loadWatchedVideos(),
        loadDownloadedVideos(),
        loadDarkModePreference(),
      ]);
      calculateStats();
    } catch (error) {
      console.error('Error loading initial data:', error);
      setError('Failed to load video library. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    if (!isOnline) return;
    
    setIsRefreshing(true);
    try {
      await loadVideos();
      calculateStats();
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const loadDarkModePreference = async () => {
    try {
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
    } catch (error) {
      console.error('Error loading dark mode preference:', error);
    }
  };

  const loadVideos = async () => {
    try {
      const SEED_VIDEOS = [
        {
          id: 'V001',
          title: 'Finite Element Analysis Explained',
          description: 'Complete guide to FEA methodology, applications, and best practices in mechanical engineering.',
          category: 'lectures',
          duration: 1845,
          views: 125400,
          likes: 8420,
          uploadDate: '2024-02-15',
          instructor: 'Prof. David Wilson',
          instructorTitle: 'PhD, Mechanical Engineering, MIT',
          thumbnail: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400',
          videoUrl: 'https://www.youtube.com/watch?v=GhrobLPmsuo',
          quality: '4K',
          size: 256,
          isFree: true,
          price: 0,
          tags: ['FEA', 'Mechanical', 'Simulation', 'Analysis'],
          chapters: [
            { time: 0, title: 'Introduction to FEA' },
            { time: 300, title: 'Mesh Generation' },
            { time: 600, title: 'Boundary Conditions' },
            { time: 900, title: 'Solving Equations' },
            { time: 1200, title: 'Results Interpretation' },
            { time: 1500, title: 'Case Studies' },
          ],
          relatedVideos: ['V002', 'V003', 'V004'],
          color: '#3b82f6',
          icon: 'cube-outline',
        },
        {
          id: 'V002',
          title: 'AutoCAD Complete Tutorial for Beginners',
          description: 'Learn AutoCAD from scratch with this comprehensive tutorial covering all essential tools and commands.',
          category: 'software',
          duration: 3780,
          views: 892300,
          likes: 45210,
          uploadDate: '2024-01-20',
          instructor: 'Eng. Sarah Chen',
          instructorTitle: 'CAD Specialist, Autodesk Certified',
          thumbnail: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400',
          videoUrl: 'https://www.youtube.com/watch?v=qZ1Rw9KZH-A',
          quality: '1080p',
          size: 512,
          isFree: true,
          price: 0,
          tags: ['AutoCAD', 'CAD', 'Design', 'Engineering Drawing'],
          chapters: [
            { time: 0, title: 'Interface Overview' },
            { time: 600, title: 'Basic Drawing Tools' },
            { time: 1200, title: 'Modifying Objects' },
            { time: 1800, title: 'Layers and Properties' },
            { time: 2400, title: 'Dimensions and Annotations' },
            { time: 3000, title: 'Printing and Plotting' },
          ],
          relatedVideos: ['V001', 'V005', 'V008'],
          color: '#ec4899',
          icon: 'draw',
        },
        {
          id: 'V003',
          title: 'Fluid Mechanics: Bernoulli\'s Equation Applications',
          description: 'Practical applications of Bernoulli\'s principle in real-world engineering problems.',
          category: 'calculations',
          duration: 1560,
          views: 324500,
          likes: 21890,
          uploadDate: '2024-03-01',
          instructor: 'Dr. Michael Rodriguez',
          instructorTitle: 'Professor of Fluid Dynamics, Imperial College',
          thumbnail: 'https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?w=400',
          videoUrl: 'https://www.youtube.com/watch?v=jHoEjvuPoB8',
          quality: '1080p',
          size: 384,
          isFree: true,
          price: 0,
          tags: ['Fluid Mechanics', 'Physics', 'Calculations', 'Bernoulli'],
          chapters: [
            { time: 0, title: 'Bernoulli\'s Equation Derivation' },
            { time: 300, title: 'Pressure Variation' },
            { time: 600, title: 'Venturi Meter Application' },
            { time: 900, title: 'Pitot Tube Measurement' },
            { time: 1200, title: 'Real-World Examples' },
          ],
          relatedVideos: ['V001', 'V004', 'V006'],
          color: '#f59e0b',
          icon: 'water',
        },
        {
          id: 'V004',
          title: 'Structural Engineering: Bridge Design Principles',
          description: 'Essential principles of bridge design and structural analysis for civil engineers.',
          category: 'demonstrations',
          duration: 2520,
          views: 456200,
          likes: 32150,
          uploadDate: '2024-02-28',
          instructor: 'Dr. Robert Johnson',
          instructorTitle: 'Structural Engineer, PE License',
          thumbnail: 'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=400',
          videoUrl: 'https://www.youtube.com/watch?v=kxXp9pl5kgk',
          quality: '4K',
          size: 768,
          isFree: false,
          price: 19.99,
          tags: ['Structural', 'Bridge Design', 'Civil Engineering', 'Construction'],
          chapters: [
            { time: 0, title: 'Types of Bridges' },
            { time: 600, title: 'Load Analysis' },
            { time: 1200, title: 'Material Selection' },
            { time: 1800, title: 'Design Standards' },
            { time: 2400, title: 'Case Study: Golden Gate Bridge' },
          ],
          relatedVideos: ['V001', 'V003', 'V007'],
          color: '#8b5cf6',
          icon: 'bridge',
        },
        {
          id: 'V005',
          title: 'MATLAB Programming for Engineers',
          description: 'Learn MATLAB programming with engineering applications and problem-solving techniques.',
          category: 'tutorials',
          duration: 2940,
          views: 678900,
          likes: 41230,
          uploadDate: '2024-01-10',
          instructor: 'Prof. Alex Kim',
          instructorTitle: 'Computational Engineering, Stanford University',
          thumbnail: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400',
          videoUrl: 'https://www.youtube.com/watch?v=Xb05CaG7TsQ',
          quality: '1080p',
          size: 640,
          isFree: true,
          price: 0,
          tags: ['MATLAB', 'Programming', 'Computational', 'Engineering'],
          chapters: [
            { time: 0, title: 'MATLAB Basics' },
            { time: 600, title: 'Matrix Operations' },
            { time: 1200, title: 'Plotting and Visualization' },
            { time: 1800, title: 'Functions and Scripts' },
            { time: 2400, title: 'Engineering Applications' },
          ],
          relatedVideos: ['V002', 'V006', 'V009'],
          color: '#10b981',
          icon: 'function',
        },
        {
          id: 'V006',
          title: 'Thermodynamics: Heat Engine Efficiency',
          description: 'Understanding heat engine cycles and calculating efficiency in thermodynamic systems.',
          category: 'lectures',
          duration: 1980,
          views: 289400,
          likes: 18760,
          uploadDate: '2024-02-05',
          instructor: 'Dr. Emily Watson',
          instructorTitle: 'Thermodynamics Expert, Cambridge University',
          thumbnail: 'https://images.unsplash.com/photo-1576086213369-97a306d36557?w=400',
          videoUrl: 'https://www.youtube.com/watch?v=8Z8Eag5Hd1A',
          quality: '4K',
          size: 512,
          isFree: false,
          price: 24.99,
          tags: ['Thermodynamics', 'Heat Engines', 'Efficiency', 'Physics'],
          chapters: [
            { time: 0, title: 'Carnot Cycle' },
            { time: 300, title: 'Otto Cycle' },
            { time: 600, title: 'Diesel Cycle' },
            { time: 900, title: 'Efficiency Calculations' },
            { time: 1200, title: 'Real Applications' },
            { time: 1500, title: 'Advanced Concepts' },
          ],
          relatedVideos: ['V003', 'V005', 'V008'],
          color: '#ef4444',
          icon: 'fire',
        },
        {
          id: 'V007',
          title: 'Interview with SpaceX Senior Engineer',
          description: 'Exclusive interview discussing rocket engineering challenges and innovations.',
          category: 'interviews',
          duration: 2460,
          views: 1256000,
          likes: 89210,
          uploadDate: '2024-03-10',
          instructor: 'James Patterson',
          instructorTitle: 'Senior Engineer, SpaceX',
          thumbnail: 'https://images.unsplash.com/photo-1517976547714-720226b864c1?w=400',
          videoUrl: 'https://www.youtube.com/watch?v=Wevm2rqQbWo',
          quality: '4K',
          size: 896,
          isFree: true,
          price: 0,
          tags: ['SpaceX', 'Rocket', 'Aerospace', 'Interview'],
          chapters: [
            { time: 0, title: 'Career Journey' },
            { time: 600, title: 'Rocket Design Challenges' },
            { time: 1200, title: 'Materials Innovation' },
            { time: 1800, title: 'Future of Space Travel' },
            { time: 2400, title: 'Advice for Engineers' },
          ],
          relatedVideos: ['V004', 'V009', 'V010'],
          color: '#06b6d4',
          icon: 'rocket',
        },
        {
          id: 'V008',
          title: 'SolidWorks Advanced Assembly Techniques',
          description: 'Master advanced assembly techniques and best practices in SolidWorks.',
          category: 'software',
          duration: 3120,
          views: 543200,
          likes: 32450,
          uploadDate: '2024-01-25',
          instructor: 'Maria Gonzalez',
          instructorTitle: 'SolidWorks Expert, CSWP Certified',
          thumbnail: 'https://images.unsplash.com/photo-1593941707882-a5bba5338fe2?w=400',
          videoUrl: 'https://www.youtube.com/watch?v=tQYW6Y5g0Tc',
          quality: '1080p',
          size: 768,
          isFree: false,
          price: 29.99,
          tags: ['SolidWorks', 'CAD', 'Assembly', '3D Modeling'],
          chapters: [
            { time: 0, title: 'Assembly Basics' },
            { time: 600, title: 'Mates and Constraints' },
            { time: 1200, title: 'Sub-assemblies' },
            { time: 1800, title: 'Motion Study' },
            { time: 2400, title: 'Large Assembly Management' },
            { time: 3000, title: 'Best Practices' },
          ],
          relatedVideos: ['V002', 'V005', 'V009'],
          color: '#8b5cf6',
          icon: 'cube',
        },
        {
          id: 'V009',
          title: 'Renewable Energy Systems Design',
          description: 'Design principles for solar, wind, and hybrid renewable energy systems.',
          category: 'research',
          duration: 2640,
          views: 398700,
          likes: 25680,
          uploadDate: '2024-02-20',
          instructor: 'Dr. Thomas Green',
          instructorTitle: 'Renewable Energy Researcher, NREL',
          thumbnail: 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=400',
          videoUrl: 'https://www.youtube.com/watch?v=1kUE0BZtTRc',
          quality: '4K',
          size: 640,
          isFree: true,
          price: 0,
          tags: ['Renewable Energy', 'Solar', 'Wind', 'Sustainability'],
          chapters: [
            { time: 0, title: 'Solar Panel Design' },
            { time: 600, title: 'Wind Turbine Systems' },
            { time: 1200, title: 'Energy Storage' },
            { time: 1800, title: 'Grid Integration' },
            { time: 2400, title: 'Case Studies' },
          ],
          relatedVideos: ['V006', 'V007', 'V010'],
          color: '#14b8a6',
          icon: 'solar-power',
        },
        {
          id: 'V010',
          title: 'Engineering Ethics and Professional Practice',
          description: 'Understanding ethical responsibilities and professional conduct in engineering.',
          category: 'lectures',
          duration: 1800,
          views: 215300,
          likes: 15640,
          uploadDate: '2024-03-05',
          instructor: 'Prof. Linda Carter',
          instructorTitle: 'Ethics Professor, Harvard University',
          thumbnail: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400',
          videoUrl: 'https://www.youtube.com/watch?v=8mAITcNt710',
          quality: '1080p',
          size: 512,
          isFree: true,
          price: 0,
          tags: ['Ethics', 'Professional Practice', 'Engineering', 'Standards'],
          chapters: [
            { time: 0, title: 'Engineering Codes of Ethics' },
            { time: 300, title: 'Case Studies' },
            { time: 600, title: 'Professional Responsibility' },
            { time: 900, title: 'Legal Considerations' },
            { time: 1200, title: 'Global Perspectives' },
            { time: 1500, title: 'Future Challenges' },
          ],
          relatedVideos: ['V001', 'V004', 'V007'],
          color: '#3b82f6',
          icon: 'scale-balance',
        },
      ];

      // Try the backend first; fall back to the curated seed when the
      // videos table is empty or unavailable so the library is never blank.
      let list = SEED_VIDEOS;
      try {
        const res = await fetch(`${API_BASE}/api/data/videos?limit=200`, {
          headers: { 'x-api-key': API_KEY },
        }).then((r) => r.json()).catch(() => null);
        if (res && res.success && Array.isArray(res.data) && res.data.length) {
          list = res.data.map((row, idx) => {
            const price = Number(row.price ?? 0) || 0;
            return {
              id: String(row.id ?? row.video_id ?? `V${idx + 1}`),
              title: String(row.title ?? '').trim() || 'Video',
              description: String(row.description ?? '').trim(),
              category: String(row.category ?? 'lectures').trim(),
              duration: Number(row.duration ?? 0) || 0,
              views: Number(row.views ?? 0) || 0,
              likes: Number(row.likes ?? 0) || 0,
              uploadDate: String(row.upload_date ?? row.uploadDate ?? row.created_at ?? '').slice(0, 10),
              instructor: String(row.instructor ?? row.instructor_name ?? '').trim(),
              instructorTitle: String(row.instructor_title ?? row.instructorTitle ?? '').trim(),
              thumbnail: String(row.thumbnail ?? row.thumbnail_url ?? '').trim(),
              videoUrl: String(row.video_url ?? row.videoUrl ?? '').trim(),
              quality: String(row.quality ?? 'HD').trim(),
              size: Number(row.size ?? 0) || 0,
              isFree: row.is_free === true || row.isFree === true || price <= 0,
              price,
              tags: toArray(row.tags),
              chapters: Array.isArray(row.chapters) ? row.chapters : toArray(row.chapters),
              relatedVideos: toArray(row.related_videos ?? row.relatedVideos),
              color: String(row.color ?? '#3b82f6').trim(),
              icon: String(row.icon ?? 'play-circle-outline').trim(),
            };
          });
        }
      } catch (e) {
        // keep seed
      }

      setVideos(list);
      setFilteredVideos(list);
    } catch (error) {
      console.error('Error loading videos:', error);
      throw error;
    }
  };

  const loadBookmarks = async () => {
    try {
      const bookmarks = await AsyncStorage.getItem('bookmarkedVideos');
      if (bookmarks) {
        setBookmarkedVideos(JSON.parse(bookmarks));
      }
    } catch (error) {
      console.error('Error loading bookmarks:', error);
    }
  };

  const loadWatchedVideos = async () => {
    try {
      const watched = await AsyncStorage.getItem('watchedVideos');
      if (watched) {
        setWatchedVideos(JSON.parse(watched));
      }
    } catch (error) {
      console.error('Error loading watched videos:', error);
    }
  };

  const loadDownloadedVideos = async () => {
    try {
      const downloaded = await AsyncStorage.getItem('downloadedVideos');
      if (downloaded) {
        setDownloadedVideos(JSON.parse(downloaded));
      }
    } catch (error) {
      console.error('Error loading downloaded videos:', error);
    }
  };

  const calculateStats = () => {
    const total = videos.length;
    const watched = watchedVideos.length;
    const downloaded = downloadedVideos.length;
    const totalWatchTime = watched * 30;
    
    setStats({ total, watched, downloaded, totalWatchTime });
  };

  const filterVideos = () => {
    let filtered = videos;
    
    if (searchQuery.trim()) {
      filtered = filtered.filter(video =>
        String(video.title ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(video.description ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(video.instructor ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        toArray(video.tags).some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(video => video.category === selectedCategory);
    }
    
    switch (selectedFilter) {
      case 'bookmarked':
        filtered = filtered.filter(video => bookmarkedVideos.includes(video.id));
        break;
      case 'downloaded':
        filtered = filtered.filter(video => downloadedVideos.includes(video.id));
        break;
      case 'watched':
        filtered = filtered.filter(video => watchedVideos.includes(video.id));
        break;
      case 'unwatched':
        filtered = filtered.filter(video => !watchedVideos.includes(video.id));
        break;
      case 'popular':
        filtered = [...filtered].sort((a, b) => b.views - a.views);
        break;
      case 'trending':
        filtered = [...filtered].sort((a, b) => b.likes - a.likes);
        break;
      case 'featured':
        filtered = filtered.filter(video => video.isFree);
        break;
      case 'recent':
      default:
        filtered = [...filtered].sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate));
        break;
    }
    
    setFilteredVideos(filtered);
  };

  const toggleBookmark = async (videoId) => {
    try {
      let newBookmarks;
      if (bookmarkedVideos.includes(videoId)) {
        newBookmarks = bookmarkedVideos.filter(id => id !== videoId);
      } else {
        newBookmarks = [...bookmarkedVideos, videoId];
      }
      
      setBookmarkedVideos(newBookmarks);
      await AsyncStorage.setItem('bookmarkedVideos', JSON.stringify(newBookmarks));
      
      const isBookmarked = newBookmarks.includes(videoId);
      Alert.alert(
        isBookmarked ? 'Bookmarked!' : 'Bookmark Removed',
        isBookmarked ? 'Video added to your bookmarks' : 'Video removed from bookmarks'
      );
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  };

  const markAsWatched = async (videoId) => {
    try {
      let newWatched;
      if (watchedVideos.includes(videoId)) {
        newWatched = watchedVideos.filter(id => id !== videoId);
      } else {
        newWatched = [...watchedVideos, videoId];
      }
      
      setWatchedVideos(newWatched);
      await AsyncStorage.setItem('watchedVideos', JSON.stringify(newWatched));
      calculateStats();
    } catch (error) {
      console.error('Error marking as watched:', error);
    }
  };

  const playVideo = (video) => {
    if (!video.isFree && !isOnline) {
      Alert.alert('Purchase Required', 'This video requires purchase. Please connect to the internet to purchase.');
      return;
    }
    
    setSelectedVideo(video);
    setVideoModalVisible(true);
    
    // Mark as watched after 30 seconds
    setTimeout(() => {
      if (!watchedVideos.includes(video.id)) {
        markAsWatched(video.id);
      }
    }, 30000);
  };

  const handlePlaybackStatusUpdate = () => {
    // Retained as a no-op for compatibility; status is polled below.
  };

  const togglePlayback = () => {
    try {
      if (!player) return;
      if (player.playing) player.pause();
      else player.play();
    } catch (e) {}
  };

  const toggleFullScreen = async () => {
    try {
      if (videoViewRef.current) {
        if (isFullScreen && videoViewRef.current.exitFullscreen) {
          await videoViewRef.current.exitFullscreen();
        } else if (videoViewRef.current.enterFullscreen) {
          await videoViewRef.current.enterFullscreen();
        }
      }
    } catch (e) {
      // native fullscreen not available — fall back to state-only toggle
    } finally {
      setIsFullScreen((prev) => !prev);
    }
  };

  // position arrives in milliseconds from the UI; expo-video uses seconds.
  const seekVideo = (position) => {
    try { if (player) player.currentTime = position / 1000; } catch (e) {}
  };

  const changePlaybackRate = (rate) => {
    setPlaybackRate(rate);
    try { if (player) player.playbackRate = rate; } catch (e) {}
  };

  // Load (and autoplay) the selected video into the expo-video player.
  useEffect(() => {
    if (!player) return;
    const uri = selectedVideo && selectedVideo.videoUrl;
    let cancelled = false;
    (async () => {
      if (videoModalVisible && uri && !isYouTubeUrl(uri)) {
        try {
          console.log('🎬 Loading video:', uri);
          if (player.replaceAsync) {
            await player.replaceAsync({ uri });
          } else {
            player.replace({ uri });
          }
          if (cancelled) return;
          try { player.playbackRate = playbackRate; } catch (e) {}
          player.play();
          console.log('▶️ play() called for:', uri);
        } catch (e) {
          console.log('❌ Video load error:', e?.message || e);
        }
      } else {
        try { player.pause(); } catch (e) {}
      }
    })();
    return () => { cancelled = true; };
  }, [selectedVideo, videoModalVisible]);

  // Surface player status changes (helps diagnose playback issues).
  useEffect(() => {
    if (!player || !videoModalVisible) return;
    let sub;
    try {
      sub = player.addListener('statusChange', (payload) => {
        const status = payload?.status || payload;
        console.log('🎞️ Video status:', status, payload?.error ? `err=${payload.error?.message}` : '');
      });
    } catch (e) {}
    return () => { try { sub && sub.remove && sub.remove(); } catch (e) {} };
  }, [videoModalVisible]);

  // Poll player status (version-proof: reads stable synchronous properties).
  useEffect(() => {
    if (!videoModalVisible || !player) return;
    const id = setInterval(() => {
      try {
        setIsPlaying(!!player.playing);
        setPlaybackPosition((player.currentTime || 0) * 1000);
        setPlaybackDuration((player.duration || 0) * 1000);
      } catch (e) {}
    }, 500);
    return () => clearInterval(id);
  }, [videoModalVisible]);

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatViews = (views) => {
    if (views >= 1000000) return `${(views / 1000000).toFixed(1)}M`;
    if (views >= 1000) return `${(views / 1000).toFixed(1)}K`;
    return views.toString();
  };

  const getCategoryColor = (category) => {
    const categoryObj = videoCategories.find(c => c.id === category);
    return categoryObj ? categoryObj.color : '#667eea';
  };

  const renderLoading = () => (
    <View style={styles.loadingContainer}>
      <ActivityIndicator size={36} color={darkMode ? "#667eea" : "#667eea"} />
      <Text style={styles.loadingText}>Loading video library...</Text>
    </View>
  );

  const renderError = () => (
    <View style={styles.errorContainer}>
      <MaterialCommunityIcons name="alert-circle-outline" size={64} color="#ef4444" />
      <Text style={styles.errorText}>{error}</Text>
      <TouchableOpacity style={styles.retryButton} onPress={loadInitialData}>
        <Text style={styles.retryButtonText}>Retry</Text>
      </TouchableOpacity>
    </View>
  );

  const renderHeader = () => (
    <Animated.View 
      style={[
        styles.header,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <LinearGradient
        colors={darkMode ? ['#2d3748', '#4a5568'] : ['#667eea', '#764ba2']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.headerGradient}
      >
        <View style={styles.headerTop}>
          <TouchableOpacity 
            style={styles.backButton} 
            onPress={() => navigation.goBack()}
          >
            <MaterialCommunityIcons name="arrow-left" size={24} color="#ffffff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>🎬 Video Library</Text>
          <TouchableOpacity 
            style={styles.downloadsButton}
            onPress={() => navigation.navigate('Downloads')}
          >
            <MaterialCommunityIcons name="download" size={24} color="#ffffff" />
          </TouchableOpacity>
        </View>
        
        <Text style={styles.headerSubtitle}>
          Browse our collection of engineering lectures, tutorials, and demonstrations
        </Text>
      </LinearGradient>
    </Animated.View>
  );

  const renderStats = () => (
    <Animated.View 
      style={[
        styles.statsContainer,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <Text style={styles.statsTitle}>📊 Video Library Stats</Text>
      
      <View style={styles.statsGrid}>
        <View style={styles.statCard}>
          <LinearGradient
            colors={darkMode ? ['#38a169', '#68d391'] : ['#4facfe', '#00f2fe']}
            style={styles.statGradient}
          >
            <MaterialCommunityIcons name="play-circle" size={24} color="#ffffff" />
          </LinearGradient>
          <Text style={styles.statNumber}>{stats.total}</Text>
          <Text style={styles.statLabel}>Total Videos</Text>
        </View>
        
        <View style={styles.statCard}>
          <LinearGradient
            colors={darkMode ? ['#3182ce', '#63b3ed'] : ['#2196f3', '#21cbf3']}
            style={styles.statGradient}
          >
            <MaterialCommunityIcons name="eye-check" size={24} color="#ffffff" />
          </LinearGradient>
          <Text style={styles.statNumber}>{stats.watched}</Text>
          <Text style={styles.statLabel}>Watched</Text>
        </View>
        
        <View style={styles.statCard}>
          <LinearGradient
            colors={darkMode ? ['#d69e2e', '#ecc94b'] : ['#43e97b', '#38f9d7']}
            style={styles.statGradient}
          >
            <MaterialCommunityIcons name="download" size={24} color="#ffffff" />
          </LinearGradient>
          <Text style={styles.statNumber}>{stats.downloaded}</Text>
          <Text style={styles.statLabel}>Downloaded</Text>
        </View>
        
        <View style={styles.statCard}>
          <LinearGradient
            colors={darkMode ? ['#805ad5', '#b794f4'] : ['#d299c2', '#fef9d7']}
            style={styles.statGradient}
          >
            <MaterialCommunityIcons name="clock" size={24} color="#ffffff" />
          </LinearGradient>
          <Text style={styles.statNumber}>{stats.totalWatchTime}</Text>
          <Text style={styles.statLabel}>Minutes</Text>
        </View>
      </View>
    </Animated.View>
  );

  const renderSearchBar = () => (
    <View style={styles.searchContainer}>
      <MaterialCommunityIcons name="magnify" size={24} color={darkMode ? "#a0aec0" : "#6b7280"} />
      <TextInput
        style={styles.searchInput}
        placeholder="Search videos, instructors, topics..."
        value={searchQuery}
        onChangeText={setSearchQuery}
        placeholderTextColor={darkMode ? "#a0aec0" : "#6b7280"}
      />
      {searchQuery.length > 0 && (
        <TouchableOpacity onPress={() => setSearchQuery('')}>
          <MaterialCommunityIcons name="close-circle" size={20} color={darkMode ? "#a0aec0" : "#6b7280"} />
        </TouchableOpacity>
      )}
    </View>
  );

  const renderCategoryFilter = () => (
    <ScrollView 
      horizontal 
      showsHorizontalScrollIndicator={false} 
      style={styles.categoryContainer}
    >
      {videoCategories.map((category) => (
        <TouchableOpacity
          key={category.id}
          style={[
            styles.categoryChip,
            selectedCategory === category.id && styles.categoryChipActive,
            { borderColor: category.color }
          ]}
          onPress={() => setSelectedCategory(category.id)}
        >
          <MaterialCommunityIcons 
            name={category.icon} 
            size={16} 
            color={selectedCategory === category.id ? '#ffffff' : category.color} 
          />
          <Text style={[
            styles.categoryText,
            selectedCategory === category.id && styles.categoryTextActive,
            { color: selectedCategory === category.id ? '#ffffff' : category.color }
          ]}>
            {category.label}
          </Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  const renderVideoFilter = () => (
    <ScrollView 
      horizontal 
      showsHorizontalScrollIndicator={false} 
      style={styles.filterContainer}
    >
      {filters.map((filter) => (
        <TouchableOpacity
          key={filter.id}
          style={[
            styles.filterChip,
            selectedFilter === filter.id && styles.filterChipActive,
          ]}
          onPress={() => setSelectedFilter(filter.id)}
        >
          <MaterialCommunityIcons 
            name={filter.icon} 
            size={14} 
            color={selectedFilter === filter.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
          />
          <Text style={[
            styles.filterText,
            selectedFilter === filter.id && styles.filterTextActive,
          ]}>
            {filter.label}
          </Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  const renderVideoCard = (video) => {
    const isBookmarked = bookmarkedVideos.includes(video.id);
    const isWatched = watchedVideos.includes(video.id);
    const isDownloaded = downloadedVideos.includes(video.id);
    
    return (
      <TouchableOpacity
        key={video.id}
        style={styles.videoCard}
        onPress={() => playVideo(video)}
      >
        {/* Video Thumbnail */}
        <View style={styles.thumbnailContainer}>
          <Image
            source={{ uri: (video.thumbnail && typeof video.thumbnail=="string" && video.thumbnail.startsWith("http")) ? video.thumbnail : undefined }}
            style={styles.thumbnail}
            resizeMode="cover"
          />
          
          {/* Duration Badge */}
          <View style={styles.durationBadge}>
            <Text style={styles.durationText}>{formatDuration(video.duration)}</Text>
          </View>
          
          {/* Play Button Overlay */}
          <View style={styles.playButtonOverlay}>
            <MaterialCommunityIcons name="play-circle" size={48} color="rgba(255,255,255,0.9)" />
          </View>
          
          {/* Watched Indicator */}
          {isWatched && (
            <View style={styles.watchedIndicator}>
              <MaterialCommunityIcons name="check-circle" size={20} color="#10b981" />
            </View>
          )}
          
          {/* Premium Badge */}
          {!video.isFree && (
            <View style={styles.premiumBadge}>
              <MaterialCommunityIcons name="crown" size={14} color="#f59e0b" />
              <Text style={styles.premiumText}>PREMIUM</Text>
            </View>
          )}
        </View>
        
        {/* Video Info */}
        <View style={styles.videoInfo}>
          <View style={styles.videoHeader}>
            <View style={[styles.categoryIcon, { backgroundColor: getCategoryColor(video.category) }]}>
              <MaterialCommunityIcons name={video.icon} size={16} color="#ffffff" />
            </View>
            
            <View style={styles.videoTitleContainer}>
              <Text style={styles.videoTitle} numberOfLines={2}>{video.title}</Text>
              <Text style={styles.videoInstructor}>{video.instructor}</Text>
            </View>
            
            <TouchableOpacity 
              onPress={() => toggleBookmark(video.id)}
              style={styles.bookmarkButton}
            >
              <MaterialCommunityIcons 
                name={isBookmarked ? "bookmark" : "bookmark-outline"} 
                size={20} 
                color={isBookmarked ? "#667eea" : (darkMode ? "#a0aec0" : "#6b7280")} 
              />
            </TouchableOpacity>
          </View>
          
          <Text style={styles.videoDescription} numberOfLines={2}>
            {video.description}
          </Text>
          
          <View style={styles.videoStats}>
            <View style={styles.statItem}>
              <MaterialCommunityIcons name="eye" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
              <Text style={styles.statText}>{formatViews(video.views)} views</Text>
            </View>
            
            <View style={styles.statItem}>
              <MaterialCommunityIcons name="thumb-up" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
              <Text style={styles.statText}>{formatViews(video.likes)}</Text>
            </View>
            
            <View style={styles.statItem}>
              <MaterialCommunityIcons name="calendar" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
              <Text style={styles.statText}>
                {new Date(video.uploadDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </Text>
            </View>
          </View>
          
          {/* Tags */}
          <View style={styles.tagsContainer}>
            {toArray(video.tags).slice(0, 2).map((tag, index) => (
              <View key={index} style={styles.tag}>
                <Text style={styles.tagText}>#{tag}</Text>
              </View>
            ))}
            {toArray(video.tags).length > 2 && (
              <View style={styles.moreTag}>
                <Text style={styles.moreTagText}>+{toArray(video.tags).length - 2}</Text>
              </View>
            )}
          </View>
          
          {/* Action Buttons */}
          <View style={styles.videoActions}>
            <TouchableOpacity 
              style={[styles.actionButton, styles.playButton]}
              onPress={() => playVideo(video)}
            >
              <MaterialCommunityIcons name="play" size={16} color="#ffffff" />
              <Text style={styles.actionButtonText}>Play</Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={[styles.actionButton, styles.bookmarkActionButton]}
              onPress={() => toggleBookmark(video.id)}
            >
              <MaterialCommunityIcons 
                name={isBookmarked ? "bookmark" : "bookmark-outline"} 
                size={16} 
                color={darkMode ? "#a0aec0" : "#6b7280"} 
              />
              <Text style={styles.bookmarkActionText}>
                {isBookmarked ? 'Saved' : 'Save'}
              </Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={[styles.actionButton, styles.shareButton]}
              onPress={() => Share.share({
                title: video.title,
                message: `Check out this engineering video: ${video.title}\n\n${video.description}`,
              })}
            >
              <MaterialCommunityIcons name="share-variant" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
            </TouchableOpacity>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const renderVideoPlayerModal = () => (
    <Modal
      visible={videoModalVisible}
      animationType="slide"
      onRequestClose={() => setVideoModalVisible(false)}
    >
      {selectedVideo && (
        <SafeAreaView style={styles.modalContainer} edges={['bottom']}>
          {/* Close bar above the player for YouTube (WebView swallows overlay taps) */}
          {isYouTubeUrl(selectedVideo.videoUrl) && (
            <View style={styles.youtubeTopBar}>
              <TouchableOpacity
                style={styles.youtubeCloseButton}
                onPress={() => setVideoModalVisible(false)}
                hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
              >
                <MaterialCommunityIcons name="close" size={26} color="#ffffff" />
              </TouchableOpacity>
              <Text style={styles.youtubeTopBarTitle} numberOfLines={1}>
                {selectedVideo.title}
              </Text>
              <TouchableOpacity
                style={styles.youtubeOpenButton}
                onPress={() => Linking.openURL(`https://www.youtube.com/watch?v=${getYouTubeId(selectedVideo.videoUrl)}`)}
                hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
              >
                <MaterialCommunityIcons name="youtube" size={22} color="#ff0000" />
                <Text style={styles.youtubeOpenText}>Open</Text>
              </TouchableOpacity>
            </View>
          )}
          {/* Video Player */}
          <View style={styles.videoPlayerContainer}>
            {isYouTubeUrl(selectedVideo.videoUrl) ? (
              <WebView
                style={styles.videoPlayer}
                source={{ html: youTubeEmbedHtml(getYouTubeId(selectedVideo.videoUrl)) }}
                allowsInlineMediaPlayback
                mediaPlaybackRequiresUserAction={false}
                javaScriptEnabled
                domStorageEnabled
                allowsFullscreenVideo
                originWhitelist={['*']}
              />
            ) : (
              <VideoView
                ref={videoViewRef}
                player={player}
                style={styles.videoPlayer}
                nativeControls={false}
                contentFit="contain"
              />
            )}
            
            {/* Custom Controls (only for non-YouTube; YouTube has its own) */}
            {showControls && !isYouTubeUrl(selectedVideo.videoUrl) && (
              <TouchableWithoutFeedback onPress={() => setShowControls(!showControls)}>
                <View style={styles.controlsOverlay}>
                  {/* Top Controls */}
                  <LinearGradient
                    colors={['rgba(0,0,0,0.8)', 'transparent']}
                    style={styles.topControls}
                  >
                    <TouchableOpacity 
                      style={styles.backControlButton}
                      onPress={() => setVideoModalVisible(false)}
                    >
                      <MaterialCommunityIcons name="arrow-left" size={24} color="#ffffff" />
                    </TouchableOpacity>
                    
                    <View style={styles.videoInfoControls}>
                      <Text style={styles.videoTitleControls} numberOfLines={1}>
                        {selectedVideo.title}
                      </Text>
                      <Text style={styles.videoInstructorControls}>
                        {selectedVideo.instructor}
                      </Text>
                    </View>
                    
                    <View style={styles.topRightControls}>
                      <TouchableOpacity 
                        style={styles.controlButton}
                        onPress={toggleFullScreen}
                      >
                        <MaterialCommunityIcons 
                          name={isFullScreen ? "fullscreen-exit" : "fullscreen"} 
                          size={24} 
                          color="#ffffff" 
                        />
                      </TouchableOpacity>
                    </View>
                  </LinearGradient>
                  
                  {/* Center Play/Pause Button */}
                  <TouchableOpacity 
                    style={styles.centerPlayButton}
                    onPress={togglePlayback}
                  >
                    <MaterialCommunityIcons 
                      name={isPlaying ? "pause-circle" : "play-circle"} 
                      size={64} 
                      color="rgba(255,255,255,0.9)" 
                    />
                  </TouchableOpacity>
                  
                  {/* Bottom Controls */}
                  <LinearGradient
                    colors={['transparent', 'rgba(0,0,0,0.8)']}
                    style={styles.bottomControls}
                  >
                    {/* Progress Bar */}
                    <View style={styles.progressContainer}>
                      <Text style={styles.timeText}>
                        {formatDuration(Math.floor(playbackPosition / 1000))}
                      </Text>
                      
                      <View style={styles.progressBar}>
                        <TouchableWithoutFeedback
                          onPress={(event) => {
                            const progressBarWidth = width - 160;
                            const position = (event.nativeEvent.locationX / progressBarWidth) * playbackDuration;
                            seekVideo(position);
                          }}
                        >
                          <View style={styles.progressTrack}>
                            <View 
                              style={[
                                styles.progressFill,
                                { 
                                  width: playbackDuration > 0 
                                    ? `${(playbackPosition / playbackDuration) * 100}%` 
                                    : '0%' 
                                }
                              ]} 
                            />
                          </View>
                        </TouchableWithoutFeedback>
                      </View>
                      
                      <Text style={styles.timeText}>
                        {formatDuration(Math.floor(playbackDuration / 1000))}
                      </Text>
                    </View>
                    
                    {/* Bottom Actions */}
                    <View style={styles.bottomActions}>
                      <TouchableOpacity 
                        style={styles.bottomActionButton}
                        onPress={() => toggleBookmark(selectedVideo.id)}
                      >
                        <MaterialCommunityIcons 
                          name={bookmarkedVideos.includes(selectedVideo.id) ? "bookmark" : "bookmark-outline"} 
                          size={24} 
                          color="#ffffff" 
                        />
                        <Text style={styles.bottomActionText}>
                          {bookmarkedVideos.includes(selectedVideo.id) ? 'Saved' : 'Save'}
                        </Text>
                      </TouchableOpacity>
                      
                      <TouchableOpacity 
                        style={styles.bottomActionButton}
                        onPress={togglePlayback}
                      >
                        <MaterialCommunityIcons 
                          name={isPlaying ? "pause" : "play"} 
                          size={24} 
                          color="#ffffff" 
                        />
                        <Text style={styles.bottomActionText}>
                          {isPlaying ? 'Pause' : 'Play'}
                        </Text>
                      </TouchableOpacity>
                      
                      <TouchableOpacity 
                        style={styles.bottomActionButton}
                        onPress={() => changePlaybackRate(
                          playbackSpeeds[(playbackSpeeds.indexOf(playbackRate) + 1) % playbackSpeeds.length]
                        )}
                      >
                        <Text style={styles.playbackRateText}>{playbackRate}x</Text>
                        <Text style={styles.bottomActionText}>Speed</Text>
                      </TouchableOpacity>
                    </View>
                  </LinearGradient>
                </View>
              </TouchableWithoutFeedback>
            )}

            {/* YouTube close is handled by the top bar above the player */}
            
            {/* Show Controls Button */}
            {!showControls && (
              <TouchableOpacity 
                style={styles.showControlsButton}
                onPress={() => setShowControls(true)}
              >
                <MaterialCommunityIcons name="dots-horizontal" size={24} color="#ffffff" />
              </TouchableOpacity>
            )}
          </View>
          
          {/* Video Details */}
          <ScrollView style={styles.videoDetailsContainer}>
            <View style={styles.videoDetailsHeader}>
              <Text style={styles.videoDetailsTitle}>{selectedVideo.title}</Text>
              <View style={styles.videoMeta}>
                <Text style={styles.videoViews}>{formatViews(selectedVideo.views)} views</Text>
                <Text style={styles.videoUploadDate}>
                  {new Date(selectedVideo.uploadDate).toLocaleDateString()}
                </Text>
              </View>
            </View>
            
            <View style={styles.instructorInfo}>
              <View style={[styles.instructorAvatar, { backgroundColor: getCategoryColor(selectedVideo.category) }]}>
                <Text style={styles.instructorInitial}>
                  {selectedVideo.instructor.charAt(0)}
                </Text>
              </View>
              <View style={styles.instructorDetails}>
                <Text style={styles.instructorName}>{selectedVideo.instructor}</Text>
                <Text style={styles.instructorTitle}>{selectedVideo.instructorTitle}</Text>
              </View>
            </View>
            
            <Text style={styles.videoDescriptionFull}>{selectedVideo.description}</Text>
            
            {/* Chapters */}
            <Text style={styles.sectionTitle}>Chapters</Text>
            <View style={styles.chaptersContainer}>
              {toArray(selectedVideo.chapters).map((chapter, index) => (
                <TouchableOpacity
                  key={index}
                  style={styles.chapterItem}
                  onPress={() => seekVideo(chapter.time * 1000)}
                >
                  <View style={styles.chapterNumber}>
                    <Text style={styles.chapterNumberText}>{index + 1}</Text>
                  </View>
                  <View style={styles.chapterContent}>
                    <Text style={styles.chapterTitle}>{chapter.title}</Text>
                    <Text style={styles.chapterTime}>
                      {formatDuration(chapter.time)}
                    </Text>
                  </View>
                  <MaterialCommunityIcons name="play-circle" size={20} color="#667eea" />
                </TouchableOpacity>
              ))}
            </View>
          </ScrollView>
        </SafeAreaView>
      )}
    </Modal>
  );

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <MaterialCommunityIcons name="video-off" size={64} color={darkMode ? "#4a5568" : "#d1d5db"} />
      <Text style={styles.emptyStateTitle}>No videos found</Text>
      <Text style={styles.emptyStateText}>
        {searchQuery ? 'Try a different search term' : 'Check back for new video content'}
      </Text>
      <TouchableOpacity 
        style={styles.resetButton}
        onPress={() => {
          setSearchQuery('');
          setSelectedFilter('recent');
          setSelectedCategory('all');
        }}
      >
        <Text style={styles.resetButtonText}>Reset Filters</Text>
      </TouchableOpacity>
    </View>
  );

  const styles = StyleSheet.create({
    safeArea: {
      flex: 1,
      backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
    },
    loadingText: {
      marginTop: 16,
      fontSize: 16,
      color: darkMode ? '#cbd5e0' : '#6b7280',
    },
    errorContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
      paddingHorizontal: 32,
    },
    errorText: {
      fontSize: 16,
      color: darkMode ? '#cbd5e0' : '#374151',
      textAlign: 'center',
      marginVertical: 16,
    },
    retryButton: {
      backgroundColor: '#667eea',
      paddingHorizontal: 32,
      paddingVertical: 12,
      borderRadius: 8,
    },
    retryButtonText: {
      color: '#ffffff',
      fontSize: 16,
      fontWeight: '600',
    },
    header: {
      marginBottom: 16,
    },
    headerGradient: {
      paddingHorizontal: 20,
      paddingTop: Platform.OS === 'ios' ? 24 : 40,
      paddingBottom: 30,
      borderBottomLeftRadius: 20,
      borderBottomRightRadius: 20,
    },
    headerTop: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 16,
    },
    backButton: {
      padding: 8,
    },
    headerTitle: {
      fontSize: 28,
      fontWeight: 'bold',
      color: '#ffffff',
      textAlign: 'center',
      flex: 1,
      textShadowColor: 'rgba(0, 0, 0, 0.3)',
      textShadowOffset: { width: 1, height: 1 },
      textShadowRadius: 3,
    },
    downloadsButton: {
      padding: 8,
    },
    headerSubtitle: {
      fontSize: 16,
      color: '#ffffff',
      opacity: 0.9,
      textAlign: 'center',
      lineHeight: 24,
    },
    statsContainer: {
      backgroundColor: darkMode ? '#2d3748' : '#ffffff',
      marginHorizontal: 20,
      marginBottom: 16,
      padding: 20,
      borderRadius: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    statsTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      color: darkMode ? '#f7fafc' : '#1f2937',
      marginBottom: 16,
    },
    statsGrid: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    statCard: {
      alignItems: 'center',
      flex: 1,
    },
    statGradient: {
      width: 50,
      height: 50,
      borderRadius: 25,
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: 8,
    },
    statNumber: {
      fontSize: 20,
      fontWeight: 'bold',
      color: darkMode ? '#f7fafc' : '#1f2937',
      marginBottom: 4,
    },
    statLabel: {
      fontSize: 12,
      color: darkMode ? '#a0aec0' : '#6b7280',
      textAlign: 'center',
    },
    searchContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: darkMode ? '#2d3748' : '#ffffff',
      marginHorizontal: 20,
      marginBottom: 12,
      borderRadius: 12,
      paddingHorizontal: 16,
      paddingVertical: 8,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 2,
    },
    searchInput: {
      flex: 1,
      height: 40,
      fontSize: 16,
      color: darkMode ? '#f7fafc' : '#1f2937',
      marginLeft: 12,
    },
    categoryContainer: {
      paddingHorizontal: 20,
      marginBottom: 8,
    },
    categoryChip: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: darkMode ? '#2d3748' : '#ffffff',
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderRadius: 20,
      marginRight: 8,
      borderWidth: 1,
    },
    categoryChipActive: {
      backgroundColor: '#667eea',
      borderColor: '#667eea',
    },
    categoryText: {
      marginLeft: 6,
      fontSize: 12,
      fontWeight: '500',
    },
    categoryTextActive: {
      color: '#ffffff',
    },
    filterContainer: {
      paddingHorizontal: 20,
      marginBottom: 20,
    },
    filterChip: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: darkMode ? '#2d3748' : '#ffffff',
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 16,
      marginRight: 8,
      borderWidth: 1,
      borderColor: darkMode ? '#4a5568' : '#e5e7eb',
    },
    filterChipActive: {
      backgroundColor: '#667eea',
      borderColor: '#667eea',
    },
    filterText: {
      marginLeft: 4,
      fontSize: 11,
      color: darkMode ? '#cbd5e0' : '#6b7280',
    },
    filterTextActive: {
      color: '#ffffff',
      fontWeight: '600',
    },
    mainScrollView: {
      flex: 1,
    },
    mainContent: {
      paddingBottom: 100,
    },
    videosContainer: {
      paddingHorizontal: 20,
    },
    sectionTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: darkMode ? '#f7fafc' : '#1f2937',
      marginBottom: 16,
      marginTop: 8,
    },
    bottomPadding: {
      height: 100,
    },
    videoCard: {
      backgroundColor: darkMode ? '#2d3748' : '#ffffff',
      marginBottom: 16,
      borderRadius: 16,
      overflow: 'hidden',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    thumbnailContainer: {
      position: 'relative',
      height: 200,
    },
    thumbnail: {
      width: '100%',
      height: '100%',
    },
    durationBadge: {
      position: 'absolute',
      bottom: 12,
      right: 12,
      backgroundColor: 'rgba(0,0,0,0.8)',
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 6,
    },
    durationText: {
      color: '#ffffff',
      fontSize: 12,
      fontWeight: 'bold',
    },
    playButtonOverlay: {
      ...StyleSheet.absoluteFillObject,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'rgba(0,0,0,0.3)',
    },
    watchedIndicator: {
      position: 'absolute',
      top: 12,
      left: 12,
      backgroundColor: 'rgba(255,255,255,0.9)',
      borderRadius: 12,
      padding: 4,
    },
    premiumBadge: {
      position: 'absolute',
      top: 12,
      right: 12,
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: 'rgba(0,0,0,0.8)',
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 12,
    },
    premiumText: {
      fontSize: 10,
      color: '#f59e0b',
      fontWeight: 'bold',
      marginLeft: 4,
    },
    videoInfo: {
      padding: 16,
    },
    videoHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 12,
    },
    categoryIcon: {
      width: 32,
      height: 32,
      borderRadius: 16,
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 12,
    },
    videoTitleContainer: {
      flex: 1,
    },
    videoTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: darkMode ? '#f7fafc' : '#1f2937',
      marginBottom: 4,
    },
    videoInstructor: {
      fontSize: 12,
      color: darkMode ? '#a0aec0' : '#6b7280',
    },
    bookmarkButton: {
      padding: 4,
    },
    videoDescription: {
      fontSize: 14,
      color: darkMode ? '#cbd5e0' : '#4b5563',
      marginBottom: 12,
      lineHeight: 20,
    },
    videoStats: {
      flexDirection: 'row',
      marginBottom: 12,
    },
    statItem: {
      flexDirection: 'row',
      alignItems: 'center',
      marginRight: 16,
    },
    statText: {
      fontSize: 12,
      color: darkMode ? '#a0aec0' : '#6b7280',
      marginLeft: 4,
    },
    tagsContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginBottom: 12,
    },
    tag: {
      backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 12,
      marginRight: 8,
      marginBottom: 4,
    },
    tagText: {
      fontSize: 10,
      color: darkMode ? '#cbd5e0' : '#4b5563',
    },
    moreTag: {
      backgroundColor: '#667eea',
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 12,
    },
    moreTagText: {
      fontSize: 10,
      color: '#ffffff',
    },
    videoActions: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    actionButton: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderRadius: 8,
      flex: 1,
      marginRight: 8,
      justifyContent: 'center',
    },
    playButton: {
      backgroundColor: '#667eea',
    },
    bookmarkActionButton: {
      backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    },
    actionButtonText: {
      color: '#ffffff',
      fontSize: 14,
      fontWeight: '600',
      marginLeft: 4,
    },
    bookmarkActionText: {
      fontSize: 14,
      fontWeight: '600',
      marginLeft: 4,
      color: darkMode ? '#cbd5e0' : '#4b5563',
    },
    shareButton: {
      backgroundColor: 'transparent',
      padding: 8,
      flex: 0,
    },
    emptyState: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 48,
      backgroundColor: darkMode ? '#2d3748' : '#ffffff',
      borderRadius: 16,
      marginTop: 20,
      marginHorizontal: 20,
    },
    emptyStateTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: darkMode ? '#f7fafc' : '#1f2937',
      marginTop: 16,
      marginBottom: 8,
    },
    emptyStateText: {
      fontSize: 14,
      color: darkMode ? '#a0aec0' : '#6b7280',
      textAlign: 'center',
      marginBottom: 20,
    },
    resetButton: {
      backgroundColor: '#667eea',
      paddingHorizontal: 24,
      paddingVertical: 12,
      borderRadius: 8,
    },
    resetButtonText: {
      color: '#ffffff',
      fontSize: 14,
      fontWeight: '600',
    },
    fab: {
      position: 'absolute',
      bottom: 24,
      right: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 8,
    },
    fabGradient: {
      width: 56,
      height: 56,
      borderRadius: 28,
      justifyContent: 'center',
      alignItems: 'center',
    },
    fabBadge: {
      position: 'absolute',
      top: -4,
      right: -4,
      backgroundColor: '#ef4444',
      width: 20,
      height: 20,
      borderRadius: 10,
      justifyContent: 'center',
      alignItems: 'center',
    },
    fabBadgeText: {
      color: '#ffffff',
      fontSize: 10,
      fontWeight: 'bold',
    },
    modalContainer: {
      flex: 1,
      backgroundColor: '#000000',
    },
    videoPlayerContainer: {
      width: '100%',
      height: height * 0.3,
      backgroundColor: '#000000',
      position: 'relative',
    },
    videoPlayer: {
      width: '100%',
      height: '100%',
    },
    controlsOverlay: {
      ...StyleSheet.absoluteFillObject,
      justifyContent: 'space-between',
    },
    topControls: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingTop: Platform.OS === 'ios' ? 48 : 16,
      paddingBottom: 16,
    },
    backControlButton: {
      padding: 8,
    },
    videoInfoControls: {
      flex: 1,
      marginHorizontal: 12,
    },
    videoTitleControls: {
      fontSize: 16,
      fontWeight: 'bold',
      color: '#ffffff',
      marginBottom: 4,
    },
    videoInstructorControls: {
      fontSize: 12,
      color: 'rgba(255,255,255,0.8)',
    },
    topRightControls: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    controlButton: {
      padding: 8,
      marginLeft: 8,
    },
    centerPlayButton: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: [{ translateX: -32 }, { translateY: -32 }],
    },
    bottomControls: {
      paddingHorizontal: 16,
      paddingBottom: Platform.OS === 'ios' ? 34 : 16,
    },
    progressContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 16,
    },
    timeText: {
      color: '#ffffff',
      fontSize: 12,
      width: 40,
    },
    progressBar: {
      flex: 1,
      marginHorizontal: 12,
    },
    progressTrack: {
      height: 4,
      backgroundColor: 'rgba(255,255,255,0.3)',
      borderRadius: 2,
    },
    progressFill: {
      height: '100%',
      backgroundColor: '#667eea',
      borderRadius: 2,
    },
    bottomActions: {
      flexDirection: 'row',
      justifyContent: 'space-around',
    },
    bottomActionButton: {
      alignItems: 'center',
    },
    bottomActionText: {
      color: '#ffffff',
      fontSize: 10,
      marginTop: 4,
    },
    playbackRateText: {
      color: '#ffffff',
      fontSize: 14,
      fontWeight: 'bold',
    },
    showControlsButton: {
      position: 'absolute',
      top: 16,
      right: 16,
      padding: 8,
      backgroundColor: 'rgba(0,0,0,0.5)',
      borderRadius: 20,
    },
    youtubeTopBar: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 8,
      paddingVertical: 10,
      paddingTop: Platform.OS === 'ios' ? 60 : (StatusBar.currentHeight || 24) + 10,
      backgroundColor: darkMode ? '#000000' : '#0f172a',
    },
    youtubeCloseButton: {
      padding: 8,
      borderRadius: 20,
    },
    youtubeTopBarTitle: {
      flex: 1,
      marginLeft: 8,
      marginRight: 12,
      fontSize: 15,
      fontWeight: '600',
      color: '#ffffff',
    },
    youtubeOpenButton: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 10,
      paddingVertical: 6,
      backgroundColor: 'rgba(255,255,255,0.12)',
      borderRadius: 16,
    },
    youtubeOpenText: {
      marginLeft: 4,
      fontSize: 13,
      fontWeight: '600',
      color: '#ffffff',
    },
    videoDetailsContainer: {
      flex: 1,
      backgroundColor: darkMode ? '#1a202c' : '#ffffff',
      padding: 20,
    },
    videoDetailsHeader: {
      marginBottom: 20,
    },
    videoDetailsTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      color: darkMode ? '#f7fafc' : '#1f2937',
      marginBottom: 8,
    },
    videoMeta: {
      flexDirection: 'row',
    },
    videoViews: {
      fontSize: 14,
      color: darkMode ? '#a0aec0' : '#6b7280',
      marginRight: 16,
    },
    videoUploadDate: {
      fontSize: 14,
      color: darkMode ? '#a0aec0' : '#6b7280',
    },
    instructorInfo: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: darkMode ? '#2d3748' : '#f3f4f6',
      padding: 16,
      borderRadius: 12,
      marginBottom: 20,
    },
    instructorAvatar: {
      width: 48,
      height: 48,
      borderRadius: 24,
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 12,
    },
    instructorInitial: {
      fontSize: 20,
      fontWeight: 'bold',
      color: '#ffffff',
    },
    instructorDetails: {
      flex: 1,
    },
    instructorName: {
      fontSize: 16,
      fontWeight: '600',
      color: darkMode ? '#f7fafc' : '#1f2937',
      marginBottom: 4,
    },
    instructorTitle: {
      fontSize: 14,
      color: darkMode ? '#a0aec0' : '#6b7280',
    },
    videoDescriptionFull: {
      fontSize: 16,
      color: darkMode ? '#cbd5e0' : '#4b5563',
      lineHeight: 24,
      marginBottom: 24,
    },
    chaptersContainer: {
      marginBottom: 24,
    },
    chapterItem: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: darkMode ? '#2d3748' : '#f3f4f6',
      padding: 12,
      borderRadius: 8,
      marginBottom: 8,
    },
    chapterNumber: {
      width: 32,
      height: 32,
      borderRadius: 16,
      backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 12,
    },
    chapterNumberText: {
      fontSize: 14,
      fontWeight: 'bold',
      color: darkMode ? '#cbd5e0' : '#4b5563',
    },
    chapterContent: {
      flex: 1,
    },
    chapterTitle: {
      fontSize: 14,
      fontWeight: '600',
      color: darkMode ? '#f7fafc' : '#1f2937',
      marginBottom: 2,
    },
    chapterTime: {
      fontSize: 12,
      color: darkMode ? '#a0aec0' : '#6b7280',
    },
  });

  if (loading) {
    return renderLoading();
  }

  if (error && !isRefreshing) {
    return renderError();
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle={darkMode ? "light-content" : "light-content"} backgroundColor="#667eea" />
      
      {renderHeader()}
      
      <ScrollView 
        style={styles.mainScrollView}
        contentContainerStyle={styles.mainContent}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={refreshData}
            colors={['#667eea']}
            tintColor="#667eea"
          />
        }
      >
        {renderStats()}
        {renderSearchBar()}
        {renderCategoryFilter()}
        {renderVideoFilter()}
        
        {/* Videos Section */}
        <View style={{ paddingHorizontal: 20 }}>
          <Text style={styles.sectionTitle}>
            📹 Engineering Videos ({filteredVideos.length} videos)
          </Text>
          
          {filteredVideos.length > 0 ? (
            filteredVideos.map(video => renderVideoCard(video))
          ) : (
            renderEmptyState()
          )}
        </View>
        
        <View style={styles.bottomPadding} />
      </ScrollView>

      <TouchableOpacity 
        style={styles.fab}
        onPress={() => navigation.navigate('Downloads')}
      >
        <LinearGradient
          colors={darkMode ? ['#38a169', '#68d391'] : ['#4facfe', '#00f2fe']}
          style={styles.fabGradient}
        >
          <MaterialCommunityIcons name="download" size={24} color="#ffffff" />
          {downloadedVideos.length > 0 && (
            <View style={styles.fabBadge}>
              <Text style={styles.fabBadgeText}>{downloadedVideos.length}</Text>
            </View>
          )}
        </LinearGradient>
      </TouchableOpacity>

      {renderVideoPlayerModal()}
    </SafeAreaView>
  );
};

export default VideoLibrary;